// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import '../../styles/table-editor.css';
import { calculateTableDimensions } from '../../constants.ts';

interface TableEditorProps {
  table: any;
  onSave: () => void;
}

const TableEditor: React.FC<TableEditorProps> = ({ table, onSave }) => {
  const { updateTable } = useRestaurant();
  const [tableName, setTableName] = useState(table.name);
  const [tableShape, setTableShape] = useState(table.shape.type);
  const [tableTexture, setTableTexture] = useState(table.texture);
  const [minCovers, setMinCovers] = useState(table.minCovers);
  const [maxCovers, setMaxCovers] = useState(table.maxCovers);
  const [textColor, setTextColor] = useState(table.textColor || '#000000');
  const [chairColor, setChairColor] = useState(table.chairColor || '#8B4513');
  const [tableColor, setTableColor] = useState(table.color || '#8B4513');
  const [useColor, setUseColor] = useState(table.useColor || false);

  // Update local state when a different table is selected
  useEffect(() => {
    setTableName(table.name);
    setTableShape(table.shape.type);
    setTableTexture(table.texture);
    setMinCovers(table.minCovers);
    setMaxCovers(table.maxCovers);
    setTextColor(table.textColor || '#000000');
    setChairColor(table.chairColor || '#8B4513');
    setTableColor(table.color || '#8B4513');
    setUseColor(table.useColor || false);
  }, [table]);

  const handleSave = async () => {
    let width: number = table.shape.width;
    let height: number = table.shape.height;
    const shapeChanged = tableShape !== table.shape.type;

    if (tableShape === 'circle') {
      const dimension = Math.max(width, height);
      width = dimension;
      height = dimension;
    } else if (shapeChanged) {
      const dims = calculateTableDimensions(tableShape as any, maxCovers);
      width = dims.width;
      height = dims.height;
    }

    const shape = { type: tableShape, width, height };
    const saved = await updateTable(table.id, {
      name: tableName,
      shape,
      texture: tableTexture,
      minCovers,
      maxCovers,
      textColor,
      chairColor,
      color: tableColor,
      useColor
    });
    if (!saved) {
      toast.error('Não foi possível salvar a mesa.');
      return;
    }
    toast.success('Mesa salva com sucesso.');
    onSave();
  };

  return (
    <div className="te-editor">
      <div className="te-editor-header">
        <h3>Configurações da Mesa</h3>
        <div className="te-header-actions">
          <button className="te-close-btn" onClick={onSave}>
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="te-editor-form">
            <div className="te-form-row te-form-row-3">
              <div className="te-form-group">
                <label htmlFor="table-name">Nome da Mesa</label>
                <input
                  id="table-name"
                  type="text"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  className="te-form-control"
                />
              </div>

              <div className="te-form-group">
                <label htmlFor="min-covers">Min. Lugares</label>
                <div className="te-number-input">
                  <input
                    id="min-covers"
                    type="number"
                    min="1"
                    max={maxCovers}
                    value={minCovers}
                    onChange={(e) => setMinCovers(Math.min(parseInt(e.target.value), maxCovers))}
                    className="te-form-control"
                  />
                </div>
              </div>

              <div className="te-form-group">
                <label htmlFor="max-covers">Max. Lugares</label>
                <div className="te-number-input">
                  <input
                    id="max-covers"
                    type="number"
                    min={minCovers}
                    max="20"
                    value={maxCovers}
                    onChange={(e) => setMaxCovers(Math.max(minCovers, parseInt(e.target.value)))}
                    className="te-form-control"
                  />
                </div>
              </div>
            </div>

            <button className="te-btn-save" onClick={handleSave}>
              Salvar
            </button>
          </div>
        </div>
      );
};

export default TableEditor;
